import { ForwardedRef, forwardRef, HTMLInputAutoCompleteAttribute } from "react"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import Form from "react-bootstrap/esm/Form"
import FormLabel from "react-bootstrap/esm/FormLabel"
import CSLabel from "./CSLabel"
import styles from './css/CSInput.module.scss'


export type CSInputButton = {icon: string | JSX.Element, action?: (e:any)=>void, tooltip?: string | JSX.Element}

type Props = {
    type: string,
    controlId: string,
    autoFocus?: boolean
    label?: string | JSX.Element,
    as?: any,
    value?: string | number,
    defaultValue?: string | number,
    solid?: boolean
    borderColor?: string
    onChange?: (e:any)=>void,
    onClick?: (e:any)=>void,
    onBlur?: (e:any)=>void,
    onKeyPress?: (e:any)=>void,
    onFocus?: (e:any)=>void,
    hasError?: boolean,
    errorText?: string,
    disabled?: boolean,
    readonly?: boolean,
    mandatory?: boolean,
    width?: string
    height?: string
    className?: string
    maxlength?: number,
    min?: number
    max?: number
    step?: number,
    buttons?: CSInputButton[]
    helpInline?: JSX.Element
    placeholder?: string
    icon?: string | JSX.Element
    data_cy?: string
    onClear?: ()=>void,
    tabIndex?: number,
    autoComplete?: HTMLInputAutoCompleteAttribute
    hideKeyboard?: boolean
}

const CSInput = (props:Props, ref:ForwardedRef<HTMLInputElement>) => {

    const {as, autoFocus=false, mandatory=false, min, max, step, maxlength, className, type, controlId, label, value, defaultValue, onChange, onBlur, onClick, onKeyPress, onFocus, hasError, errorText, disabled=false, readonly=false, height, width="150px", buttons, helpInline, placeholder, solid=false, borderColor, icon, onClear, tabIndex, data_cy, autoComplete, hideKeyboard=false } = props

    const controlStyle:React.CSSProperties = {width, height, borderColor}
    if(hasError) {
        controlStyle.borderColor = '#dc3545'
    }

    return (
        <Form.Group className={className} style={{position: 'relative'}} data-cy={`select-${data_cy}`}>
            {label && <CSLabel mandatory={mandatory} label={label} helpInline={helpInline} />}
            {!readonly ? <div style={{position: 'relative', width}}>
                                {icon && <div className="cs-input-icon d-flex align-items-center">
                                    {typeof icon === "string" && <i className={icon} />}
                                    {typeof icon === "object" && icon}
                                </div>}
                                <Form.Control 
                                as={as}
                                type={type}
                                id={controlId}
                                autoFocus={autoFocus}
                                maxLength={maxlength}
                                onChange={(e)=>{onChange?.(e)}}
                                onBlur={(e)=>{onBlur?.(e)}}
                                onClick={(e)=>{onClick?.(e)}}
                                onKeyPress={(e)=>{onKeyPress?.(e)}}
                                onFocus={(e)=>{onFocus?.(e)}}
                                ref={ref}
                                value={value}
                                defaultValue={defaultValue}
                                disabled={disabled}
                                min={min}
                                max={max}
                                step={step}
                                className={`${styles.input} ${solid?styles.solid:``} ${icon?styles.icon:``} ${buttons?styles[`withbuttons-${buttons.length}`]:``}`}
                                style={controlStyle}
                                placeholder={placeholder}
                                tabIndex={tabIndex}
                                autoComplete={autoComplete}
                                readOnly={hideKeyboard}
                            />
                            {((buttons && buttons.length>0) || onClear) &&
                                <div className={`${styles.buttons} ms-3`}>
                                    {!disabled && onClear && value && ((typeof value === 'string' && value.length>0) || (value === undefined)) &&
                                        <span key={`${controlId}_btns_clearable`} role="button" className={`ms-2 ${styles.button}`}>
                                        <i className={'bi-x'} onClick={()=>{onClear()}}></i>
                                    </span>
                                    }
                                    {buttons?.map((button, index)=>{
                                        const { icon, action, tooltip } = button
                                        const tooltipComponent = tooltip?(
                                            <Tooltip id="tooltip" style={{position:'fixed'}}>
                                                {tooltip}
                                            </Tooltip>
                                        ):<></>

                                        if(tooltip) {
                                            return <OverlayTrigger key={`${controlId}_btns_${index}`} placement="top" overlay={tooltipComponent}>
                                                        <span role="button" className={`ms-2 ${styles.button}`}>
                                                            {typeof icon === "string" && <i className={icon} onClick={action}></i>}
                                                            {typeof icon === "object" && icon}
                                                        </span>    
                                                </OverlayTrigger>
                                        }
                                        
                                        return <span key={`${controlId}_btns_${icon}`} role="button" className={`ms-2 ${styles.button}`}>
                                                {typeof icon === "string" && <i className={icon} onClick={action}></i>}
                                                {typeof icon === "object" && icon}
                                            </span>
                                    })}
                                </div>
                            }
                        </div>
                : 
            <div style={{minWidth: width}}>
                <p className="text-muted small"><strong>{value || <>&nbsp;</>}</strong></p>
            </div>}
            <FormLabel className='cs-text text-danger' style={{position: 'absolute', left: 0, bottom:'-25px'}} visuallyHidden={!hasError}>
                <small>{errorText}</small>
            </FormLabel>
        </Form.Group>
    )
}

export default forwardRef(CSInput)